import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import SideNav from "component/layouts/sideNav";

import {
  ADD,
  DELETE,
  GO,
  GOODS_RECEIVED,
  MATERIAL_MANAGEMENT,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_GOODS_RECEIVED,
  PATH_GOODS_RECEIVED_DETAIL,
  PATH_LOGIN,
} from "resources/constant/url/url";
import {
  setDates,
  setFilterModel,
  setSortModel,
} from "store/Slice/goodsReceivedSlice";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APIMATERIALMGMT } from "apihandler/materialmgmt";

import {
  setItemCategories,
  setItemList,
  setItems,
  setPurchaseOrders,
  setSiteCategories,
  setSites,
  setSupplierCategories,
  setSuppliers,
  setTaxes,
  setUoms,
  setUsers,
  siteCellRenderer,
  supplierCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";

import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";
import { useDebounce } from "resources/constant/utilities/util";

const getCacheKey = (fromDate, toDate) =>
  `${fromDate.format("YYYY-MM-DD")}_${toDate.format("YYYY-MM-DD")}`;

export default function GoodsReceived() {
  let { date } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const grnCacheRef = useRef({});

  const { fromDate, toDate, filterModel, sortModel } = useSelector(
    (s) => s.grnList
  );

  const [dateValue, setDateValue] = useState(
    date ? dayjs(date) : fromDate ? dayjs(fromDate) : dayjs().startOf("month")
  );
  const [toDateValue, setToDateValue] = useState(
    toDate ? dayjs(toDate) : dayjs().endOf("month")
  );

  const gridRef = useRef();

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  useEffect(() => {
    dispatch(
      setDates({
        fromDate: dateValue ? dateValue.format("YYYY-MM-DD") : null,
        toDate: toDateValue ? toDateValue.format("YYYY-MM-DD") : null,
      })
    );
  }, [dateValue, toDateValue, dispatch]);

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const safeFrom = (dateValue ?? dayjs().startOf("month")).format("YYYY-MM-DD");
  const safeTo = (toDateValue ?? dayjs().endOf("month")).format("YYYY-MM-DD");

  const deleteModel = () => {
    if (selectedRowIds.length === 0) {
      return;
    }
    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      APIMATERIALMGMT.DeleteSelectedGoods(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current?.api?.getSelectedRows() ?? [];
            gridRef.current?.api?.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        })
        .catch(() => setAlertTitleText("Network Error", "Please try again."));
    });
  };

  const updateModel = (model) => {
    gridRef.current?.api?.applyTransaction({ update: [model] });
  };

  const addModel = (model) => {
    gridRef.current?.api?.applyTransaction({ add: [model], addIndex: 0 });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      const ids = selectedRows.map((row) => row.id);
      setSelectedRowIds(ids);
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const handleEditData = (oldData) => {
    console.log(oldData);
  };

  const columnDefs = useMemo(
    () => [
      {
        flex: 1.5,
        headerName: "GRN No",
        field: "grnNo",
        checkboxSelection: false,
        cellStyle: { "border-right-color": "#e2e2e2" },
        autoHeight: true,
        wrapText: true,
      },
      {
        flex: 1.1,
        headerName: "GRN Date",
        field: "date",
        filter: false,
        floatingFilter: false,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Supplier Name",
        field: "supplierId",
        autoHeight: true,
        wrapText: true,
        valueGetter: (params) =>
          supplierCellRenderer({ value: params.data.supplierId }),
        cellStyle: { "border-right-color": "#e2e2e2" },
      },
      {
        headerName: "Site",
        field: "siteId",
        autoHeight: true,
        wrapText: true,
        valueGetter: (params) =>
          siteCellRenderer({ value: params.data.siteId }),
        cellStyle: { "border-right-color": "#e2e2e2" },
      },
      {
        headerName: "Bill No",
        field: "billNo",
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Bill Date",
        field: "billDate",
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
        autoHeight: true,
        wrapText: true,
        filter: false,
        floatingFilter: false,
      },
      {
        headerName: "Basic Cost",
        field: "basicCost",
        filter: false,
        floatingFilter: false,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
        autoHeight: true,
        wrapText: true,
        valueFormatter: (p) =>
          p.value == null
            ? ""
            : Number(p.value).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            }),
      },
      {
        headerName: "Tax Cost",
        field: "taxCost",
        filter: false,
        floatingFilter: false,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
        autoHeight: true,
        wrapText: true,
        valueFormatter: (p) =>
          p.value == null
            ? ""
            : Number(p.value).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            }),
      },
      {
        headerName: "Grand Total",
        field: "grandTotal",
        filter: false,
        floatingFilter: false,
        cellStyle: {
          "text-align": "center",
          "border-right-color": "#e2e2e2",
        },
        autoHeight: true,
        wrapText: true,
        valueFormatter: (p) =>
          p.value == null
            ? ""
            : Number(p.value).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            }),
      },
      {
        headerName: "Modified By",
        field: "modifiedBy",
        valueGetter: (params) =>
          userCellRenderer({ value: params.data.modifiedBy }),
        cellStyle: { "border-right-color": "#e2e2e2" },
        autoHeight: true,
        wrapText: true,
      },
      {
        flex: 0.9,
        headerName: "Details",
        field: "id",
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {params.data.status !== "C" ? (
              <IconButton
                aria-label="edit"
                onClick={() => {
                  navigate(PATH_GOODS_RECEIVED_DETAIL + params.data.id, {
                    state: { date: safeFrom, isEdit: true },
                  });
                }}
              >
                <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
              </IconButton>
            ) : null}
            <IconButton
              aria-label="view"
              onClick={() => {
                navigate(PATH_GOODS_RECEIVED_DETAIL + params.data.id, {
                  state: { date: safeFrom, isEdit: false },
                });
              }}
            >
              <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          </div>
        ),
      },
    ],
    [navigate, safeFrom]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      editable: false,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        caseSensitive: false,
        defaultOption: "startsWith",
      },
      floatingFilter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    []
  );

  const fetchGrn = (d1, d2) => {
    if (!d1 || !d2 || !dayjs.isDayjs(d1) || !dayjs.isDayjs(d2)) {
      return Promise.resolve([]);
    }
    return APIMATERIALMGMT.GoodsReceiveds(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        const data = [...resp.response].sort((a, b) => b.id - a.id);
        return data;
      })
      .catch(() => {
        setAlertTitleText("Network Error", "Please try again.");
        return [];
      });
  };

  const onGridReady = async (params) => {
    APIMASTER.Uoms()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        resp.response.forEach((x) => {
          x.value = x.id;
          x.name = x.uomName;
        });
        setUoms(resp.response);
      });

    APIMASTER.TaxOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setTaxes(resp.response);
      });

    APIMASTER.ItemCategories()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setItemCategories(resp.response);
      });

    APIMATERIALMGMT.PurchaseOrderOptions("all")
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setPurchaseOrders(resp.response);
      });

    APIMASTER.SupplierOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setSuppliers(resp.response);
      });

    APIMASTER.SupplierCategories()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setSupplierCategories(resp.response);
      });

    APIMASTER.SiteCategories()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setSiteCategories(resp.response);
      });

    APIMASTER.SiteOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setSites(resp.response);
      });

    APIMASTER.UserOptions()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        setUsers(resp.response);
      });

    APIMASTER.Items()
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) return;
        const itemOptions = resp.response.map((element) => ({
          name: element.itemName,
          value: element.id,
          dependentvalue: element.categoryId,
        }));
        setItems(itemOptions);
        setItemList(resp.response);
      });

    const initFrom = date ? dayjs(date) : dateValue;
    const initTo = toDateValue;
    const data = await fetchGrn(initFrom, initTo);
    params.api.applyTransaction({ add: data });

    if (filterModel) {
      params.api.setFilterModel(filterModel);
      params.api.onFilterChanged();
    }
    if (sortModel) {
      params.api.setSortModel(sortModel);
    }
  };

  const loadGrnWithCache = async (from, to) => {
    try {
      setIsLoading(true);
      if (!from || !to) return;

      const cacheKey = getCacheKey(from, to);
      let data;

      if (grnCacheRef.current[cacheKey]) {
        data = grnCacheRef.current[cacheKey];
      } else {
        data = await fetchGrn(from, to);
        grnCacheRef.current[cacheKey] = data;
      }

      try {
        const rowData = [];
        gridRef.current?.api?.forEachNode((n) => rowData.push(n.data));
        if (rowData.length) {
          gridRef.current?.api?.applyTransaction({ remove: rowData });
        }
      } catch (_) {}

      try {
        gridRef.current?.api?.applyTransaction({ add: data });
      } catch (_) {}
    } finally {
      setIsLoading(false);
    }
  };
  const debouncedLoadGrn = useDebounce(
    (from, to) => loadGrnWithCache(from, to),
    300
  );

  const onFilterChanged = (e) => {
    const model = e.api.getFilterModel();
    dispatch(setFilterModel(model && Object.keys(model).length ? model : null));
  };
  const onSortChanged = (e) => {
    const model = e.api.getSortModel();
    dispatch(setSortModel(model && model.length ? model : null));
  };

  return (
    <SideNav
      currentComponent={MATERIAL_MANAGEMENT}
      subComponent={GOODS_RECEIVED}
      subComponentPath={PATH_GOODS_RECEIVED}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_GOODS_RECEIVED_DETAIL + 0, {
              state: { date: safeFrom, isEdit: null },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(newValue) => {
                setDateValue(newValue);
              }}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => {
                setToDateValue(newValue);
              }}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div align="right" style={{ width: "100%", right: 0 }}>
          <Button
            disabled={isLoading}
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={() => {
              setIsLoading(true);
              debouncedLoadGrn(dateValue, toDateValue);
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div style={{ overflowX: "auto", width: "100%" }}>
        <div className="App">
          <div className="ag-theme-alpine" style={{ height: "570px" }}>
            <AgGridReact
              copyGroupHeadersToClipboard={true}
              enableRangeSelection={true}
              columnDefs={columnDefs}
              ref={gridRef}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              paginationAutoPageSize={true}
              pagination={true}
              rowSelection="multiple"
              getRowId={(params) => params.data.id}
              onSelectionChanged={onRowSelectionChanged}
              onFilterChanged={onFilterChanged}
              onSortChanged={onSortChanged}
              suppressBrowserResizeObserver={true}
            />
          </div>
        </div>
      </div>
    </SideNav>
  );
}
